import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ShieldAlert, Loader2 } from "lucide-react";
import { errMsg } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public patient-facing checkout for Second Opinion orders.
 *
 * Lives at /checkout/mock/:orderId (no auth). Fetches order details via
 * the public /api/public/second-opinion/order/{id} endpoint, renders a
 * clean payment-page UI showing doctor + service + amount, and offers:
 *   • A "Pay now (test)" button → POST /api/public/second-opinion/order/{id}/mock-complete
 *     (works ONLY when the order's gateway is mock; the public API itself
 *     returns 403 if gateway=cashfree, in which case we fall back to the
 *     Cashfree JS SDK + payment_session_id).
 *
 * This page is intentionally minimal — one task: close the pending-payment
 * state for an anonymous patient so the case flips to payment_received
 * via the standard webhook idempotency path.
 */
export default function PublicCheckout() {
    const { orderId } = useParams();
    const nav = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [outcome, setOutcome] = useState(null); // null | success | failed | not_mock
    const [caseShort, setCaseShort] = useState("");
    const [slaHours, setSlaHours] = useState(null);
    const [redirectUrl, setRedirectUrl] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${BACKEND}/api/public/second-opinion/order/${orderId}`);
                setOrder(r.data);
                if (r.data.status === "successful") setOutcome("success");
                else if (r.data.status === "failed") setOutcome("failed");
            } catch (e) {
                toast.error(errMsg(e, "Order not found"));
                setTimeout(() => nav("/"), 1200);
            } finally {
                setLoading(false);
            }
        })();
    }, [orderId, nav]);

    async function payWithMock(wantSuccess = true) {
        setPaying(true);
        try {
            const { data } = await axios.post(
                `${BACKEND}/api/public/second-opinion/order/${orderId}/mock-complete`,
                { outcome: wantSuccess ? "success" : "failure" },
            );
            // Refetch — webhook state is the source of truth.
            const r = await axios.get(`${BACKEND}/api/public/second-opinion/order/${orderId}`);
            setOrder(r.data);
            if (r.data.status === "successful") {
                setOutcome("success");
                toast.success("Payment confirmed — the doctor has been notified.");
                if (data?.case_short) setCaseShort(data.case_short);
                if (data?.sla_hours) setSlaHours(data.sla_hours);
                if (data?.redirect_url) setRedirectUrl(data.redirect_url);
                // iter91 Step-0 fix · show success for ~1.8s so patients
                // see a confirmation before we send them back to the
                // doctor's contact page (which now shows its own success
                // card for the paid=so flag).
                setTimeout(() => {
                    if (data?.redirect_url) window.location.href = data.redirect_url;
                }, 1800);
            } else if (r.data.status === "failed") {
                setOutcome("failed");
                toast.error("Payment failed — please try again or contact support.");
            }
        } catch (e) {
            if (e?.response?.status === 403) {
                setOutcome("not_mock");
                toast.error("This order uses Cashfree — mock pay is not available.");
            } else {
                toast.error(errMsg(e, "Payment failed"));
            }
        } finally {
            setPaying(false);
        }
    }

    // Cashfree surface — same lazy SDK loading pattern as DoctorCheckout.
    const loadCashfreeSDK = () => new Promise((resolve, reject) => {
        if (window.Cashfree) return resolve(window.Cashfree);
        const s = document.createElement("script");
        s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
        s.async = true;
        s.onload = () => resolve(window.Cashfree);
        s.onerror = () => reject(new Error("Failed to load Cashfree SDK"));
        document.body.appendChild(s);
    });
    async function payWithCashfree() {
        if (!order?.payment_session_id) {
            toast.error("Payment session missing — recreate your request.");
            return;
        }
        setPaying(true);
        try {
            const Cashfree = await loadCashfreeSDK();
            const mode = order.sdk_mode === "production" ? "production" : "sandbox";
            const cf = Cashfree({ mode });
            await cf.checkout({
                paymentSessionId: order.payment_session_id,
                redirectTarget: "_self",
            });
        } catch (e) {
            toast.error(errMsg(e, "Cashfree checkout failed to start"));
        } finally {
            setPaying(false);
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F8FA] flex items-center justify-center" data-testid="public-checkout-loading">
                <Loader2 className="animate-spin text-[#123B5D]" size={28} />
            </div>
        );
    }
    if (!order) return null;

    const amount = Number(order.amount_inr || 0).toLocaleString("en-IN");
    const isMock = order.gateway === "mock";
    const isCashfree = order.gateway === "cashfree";

    return (
        <div className="min-h-screen bg-[#F5F8FA] flex items-start justify-center px-4 py-16" data-testid="public-checkout-page">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_12px_40px_-20px_rgba(15,27,45,0.3)] border border-[#E5E9F2] p-8">
                {outcome === "success" ? (
                    <div className="text-center" data-testid="public-checkout-success">
                        <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-3" />
                        <h1 className="text-2xl font-bold text-[#123B5D] mb-2">Payment confirmed</h1>
                        <p className="text-sm text-[#17212B]/70">
                            {order.doctor_name} will review your reports and respond
                            {slaHours ? <> within <strong>{slaHours} hours</strong></> : " shortly"}.
                            You'll receive confirmation on WhatsApp and email.
                        </p>
                        {caseShort && (
                            <div className="mt-4 inline-block px-4 py-2 bg-[#F5F8FA] border border-[#E5E9F2] rounded-full text-xs font-bold tracking-wider text-[#123B5D]"
                                data-testid="public-checkout-case-ref">
                                Case reference: {caseShort}
                            </div>
                        )}
                        <p className="text-[11px] text-[#17212B]/50 mt-4">Taking you back to the doctor's page…</p>
                    </div>
                ) : outcome === "failed" ? (
                    <div className="text-center" data-testid="public-checkout-failed">
                        <XCircle size={48} className="text-rose-500 mx-auto mb-3" />
                        <h1 className="text-2xl font-bold text-[#123B5D] mb-2">Payment failed</h1>
                        <p className="text-sm text-[#17212B]/70">Please try again or get in touch with the clinic.</p>
                    </div>
                ) : (
                    <>
                        <div className="text-center mb-6">
                            <div className="text-[11px] uppercase tracking-widest text-[#0F766E] font-bold mb-1">
                                Second Opinion
                            </div>
                            <h1 className="text-2xl font-bold text-[#123B5D]">Secure checkout</h1>
                            <p className="text-xs text-[#17212B]/60 mt-1">
                                You're paying for a professional second opinion
                            </p>
                        </div>
                        <div className="rounded-xl bg-[#F5F8FA] border border-[#E5E9F2] p-4 mb-4" data-testid="public-checkout-summary">
                            <div className="flex justify-between items-start gap-3">
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-[#64748B] font-semibold">Doctor</div>
                                    <div className="font-bold text-[#123B5D]">{order.doctor_name}</div>
                                    {order.doctor_specialization && (
                                        <div className="text-xs text-[#17212B]/60">{order.doctor_specialization}</div>
                                    )}
                                </div>
                                <div className="text-right">
                                    <div className="text-[11px] uppercase tracking-widest text-[#64748B] font-semibold">Amount</div>
                                    <div className="font-bold text-[#123B5D] text-xl" data-testid="public-checkout-amount">₹{amount}</div>
                                </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-[#E5E9F2]">
                                <div className="text-[11px] uppercase tracking-widest text-[#64748B] font-semibold">Service</div>
                                <div className="text-sm text-[#17212B]">{order.service_label}</div>
                            </div>
                        </div>
                        {isMock && (
                            <>
                                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-4 flex gap-2">
                                    <ShieldAlert size={14} className="text-amber-700 flex-shrink-0 mt-0.5" />
                                    <div className="text-[12px] text-amber-900 leading-snug">
                                        <strong>Test mode — Mock gateway.</strong> No real money is charged. In production this screen is replaced by Cashfree's hosted checkout.
                                    </div>
                                </div>
                                <button onClick={() => payWithMock(true)} disabled={paying} data-testid="public-checkout-pay-success"
                                    className="w-full bg-[#123B5D] hover:bg-[#0d2a44] text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
                                    {paying ? <Loader2 size={16} className="animate-spin" /> : null}
                                    Pay ₹{amount} (simulate success)
                                </button>
                                <button onClick={() => payWithMock(false)} disabled={paying} data-testid="public-checkout-pay-failure"
                                    className="w-full border border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold py-2.5 rounded-xl mt-2 text-sm">
                                    Simulate failure
                                </button>
                            </>
                        )}
                        {isCashfree && (
                            <>
                                <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 mb-4 text-[12px] text-sky-900">
                                    You'll be redirected to Cashfree's secure checkout.
                                </div>
                                <button onClick={payWithCashfree} disabled={paying || !order.payment_session_id}
                                    data-testid="public-checkout-pay-cashfree"
                                    className="w-full bg-[#123B5D] hover:bg-[#0d2a44] text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
                                    {paying ? <Loader2 size={16} className="animate-spin" /> : null}
                                    Pay ₹{amount} via Cashfree
                                </button>
                            </>
                        )}
                        <p className="text-[11px] text-[#17212B]/50 mt-4 text-center">
                            This is a professional opinion service — not emergency care. For emergencies, call your local emergency number.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
